import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/translations/config';

import { Link } from 'react-router-dom';
import { API_BASE_URL, datadogRumEnabled } from '@/config';

import axios from 'axios';
import { usePostHog } from 'posthog-js/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';

// core components
import GridItem from '@/components/Grid/GridsItem';
import GridContainer from '@/components/Grid/GridContainer';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import Divider from '@mui/material/Divider';

import { toast } from 'react-toastify';
import loader from '../../assets/img/loader.svg';
import FloatingLanguagePicker from '@/components/LanguagePicker/FloatingLanguagePicker';
import PoweredByTailfin from '@/components/PoweredByTailfin';
import logoimg from '@/assets/img/imgpsh_fullsize_anim_front.png';

export default function Loginfacility() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [corporateEmail, setCorporateEmail] = useState('');
  const [mfaSession, setMfaSession] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtp, setIsOtp] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState('');

  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const authorizationCode = queryParams.get('code');

    if (authorizationCode) {
      setLoading(true);
      login(false, authorizationCode)
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, []);

  const handlePassword = (event) => {
    setPassword(event.target.value);
  };
  const handleEmail = (event) => {
    setEmail(event.target.value);
  };
  const handleCorporateEmail = (event) => {
    setCorporateEmail(event.target.value);
  };
  const handleOtp = (event) => {
    setOtp(event.target.value);
  };

  const redirectToDashboard = () => {
    const redirectUrl =
      new URLSearchParams(location.search).get('redirect') ||
      '/facility/facilitydashboard';
    navigate(redirectUrl);
  };

  const login = async (isResendCode, authorizationCode) => {
    try {
      const formData = new FormData();
      if (authorizationCode) {
        formData.append('code', authorizationCode);
      } else {
        formData.append('corporateEmail', corporateEmail.trim());
        formData.append('email', email.trim());
        formData.append('password', password);
        if (!isResendCode) {
          formData.append('otp', otp);
          formData.append('mfaSession', mfaSession);
          formData.append('mfaChallenge', mfaChallenge);
        }
      }

      localStorage.removeItem('appState');
      if (datadogRumEnabled) {
        datadogRum.clearUser();
      }
      const response = await axios.post(
        API_BASE_URL + '/facility_login',
        formData,
      );

      const languagePreference = response?.data?.user?.languagePreference;
      if (languagePreference && languagePreference !== i18n.language) {
        i18n.changeLanguage(response?.data?.user?.languagePreference);
      }

      if (response.data?.ssoUrl) {
        window.location = response.data?.ssoUrl;
        return;
      }

      if (response.data?.mfa) {
        setIsOtp(true);
        setMfaSession(response.data.mfaSession);
        setMfaChallenge(response.data.mfaChallenge);
        return;
      }

      localStorage.setItem('loginEmail', response.data.user.email);
      localStorage.setItem('userName', response.data.user.name);

      localStorage['userPermissions'] = JSON.stringify(
        response?.data?.userPermissions,
      );

      if (response?.data?.resetToken) {
        localStorage.setItem('resetToken', response?.data?.resetToken);
        navigate('/user/reset');
        return;
      }
      localStorage['userId'] = JSON.stringify(response?.data?.user?.id);

      localStorage.setItem('lastActivity', new Date());

      posthog.group('organization', response.data.organization.id, {
        name: response.data.organization.name,
      });

      localStorage.setItem('organizationName', response.data.organization.name);

      posthog.identify(response.data.user.id, {
        name: response.data.user.email,
        email: response.data.user.email,
        organization: response.data.organization.name,
      });

      if (datadogRumEnabled) {
        datadogRum.setUser({
          id: response.data.user.id,
          name: response.data.user.name,
          email: response.data.user.email,
        });
      }

      posthog.capture('login');
      localStorage.setItem(
        'organizationFeatures',
        JSON.stringify(response.data.organizationFeatures),
      );
      localStorage.setItem(
        'currentOrganizationId',
        response.data.organization.id,
      );

      const userData = response.data.facilities[0];
      let appState = {
        isLoggedIn: true,
        user: {
          ...userData,
          permissionsType: response.data.user?.permissionsType,
          roleName: response.data.user?.roleName,
          hasAllFacilitiesAccess:
            response.data.user?.hasAllFacilitiesAccess ?? false,
        },
        name: userData.name,
      };

      localStorage['appState'] = JSON.stringify(appState);

      redirectToDashboard();
    } catch (err) {
      console.log(err);
      toast.error(err.response.data.errmsg, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
      }}>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      )}
      <form
        style={{
          position: 'relative',
        }}
        onSubmit={(e) => {
          e.preventDefault();
          login();
        }}>
        <GridContainer>
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                zIndex: 9999,
                transform: 'translate(-50%, -50%)',
              }}>
              <img src={loader} alt='Loading...' />
            </div>
          )}
          <GridItem xs={8} sm={8} md={6}>
            <Card>
              <CardHeader color='primary'>
                <GridContainer alignItems='center' justify='space-between'>
                  <GridItem xs={8} sm={8} md={6} className='mx-auto'>
                    <h4>{t('auth.facilityLogin.title')}</h4>
                  </GridItem>
                  <GridItem xs={8} sm={8} md={6}>
                    <img src={logoimg} width='130' />
                  </GridItem>
                </GridContainer>
              </CardHeader>
              <CardBody>
                {!isOtp ? (
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <CustomInput
                        disabled={loading}
                        labelText={t('auth.login.emailLabel')}
                        id='heading'
                        name='heading'
                        onChange={handleEmail}
                        value={email}
                        type='text'
                        formControlProps={{
                          fullWidth: true,
                        }}
                        dataTestId='inputEmail'
                      />
                    </GridItem>
                    <GridItem xs={8} sm={8} md={6}>
                      <CustomInput
                        disabled={loading}
                        labelText={t('auth.login.passwordLabel')}
                        id='subheading'
                        type='password'
                        name='subheading'
                        onChange={handlePassword}
                        value={password}
                        formControlProps={{
                          fullWidth: true,
                        }}
                        dataTestId='inputPassword'
                      />
                    </GridItem>
                  </GridContainer>
                ) : (
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <div>
                        <CustomInput
                          disabled={loading}
                          labelText={t('auth.login.otpLabel')}
                          onChange={handleOtp}
                          value={otp}
                          type='text'
                          formControlProps={{
                            fullWidth: true,
                          }}
                        />

                        <p
                          style={{
                            fontSize: '14px',
                            marginBottom: '20px',
                            color: 'grey',
                          }}>
                          {mfaChallenge === 'EMAIL_OTP'
                            ? t('auth.login.otpEmailMessage')
                            : t('auth.login.otpAuthAppMessage')}
                        </p>
                      </div>
                    </GridItem>
                    <GridItem xs={8} sm={8} md={6}></GridItem>
                  </GridContainer>
                )}
                <GridContainer alignItems='center'>
                  <GridItem xs={8} sm={8} md={6}></GridItem>
                  <GridItem xs={8} sm={8} md={6}>
                    {isOtp ? (
                      mfaChallenge === 'EMAIL_OTP' && (
                        <Link color='primary' onClick={() => login(true)}>
                          {t('auth.login.resendOtpLink')}
                        </Link>
                      )
                    ) : (
                      <Link color='primary' to='/user/forgotpassword'>
                        {t('auth.login.forgotPasswordLink')}
                      </Link>
                    )}
                  </GridItem>
                </GridContainer>
                <GridContainer alignItems='center'>
                  <GridItem xs={8} sm={8} md={6}>
                    <Button
                      disabled={loading}
                      color='primary'
                      type='submit'
                      dataTestId='loginButton'>
                      {t('auth.facilityLogin.submitButton')}
                    </Button>
                  </GridItem>
                  <GridItem xs={8} sm={8} md={6}></GridItem>
                </GridContainer>

                {!isOtp && (
                  <div>
                    <GridContainer>
                      <GridItem xs={16} sm={16} md={16}>
                        <Divider>{t('auth.facilityLogin.orDivider')}</Divider>
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={8} sm={8} md={6}>
                        <CustomInput
                          disabled={loading}
                          labelText={t(
                            'auth.facilityLogin.corporateEmailLabel',
                          )}
                          id='corporateEmail'
                          name='corporateEmail'
                          onChange={handleCorporateEmail}
                          value={corporateEmail}
                          type='text'
                          formControlProps={{
                            fullWidth: true,
                          }}
                        />
                      </GridItem>
                      <GridItem xs={8} sm={8} md={6}></GridItem>
                    </GridContainer>
                    <GridContainer alignItems='center'>
                      <GridItem xs={8} sm={8} md={6}>
                        <Button
                          disabled={loading}
                          color='primary'
                          type='submit'>
                          {t('auth.facilityLogin.ssoButton')}
                        </Button>
                      </GridItem>
                      <GridItem xs={8} sm={8} md={6}></GridItem>
                    </GridContainer>
                  </div>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}>
          <PoweredByTailfin size='medium' />
        </div>
      </form>
      <FloatingLanguagePicker />
    </div>
  );
}
